import { useState, useEffect } from 'react';
import { Message } from '@/types/chat';
import { sendMessageToOpenAI } from '@/services/openaiService';
import { useToast } from '@/hooks/use-toast';
import { loadChatHistory, saveChatHistory } from '@/utils/cookieUtils';
import { useFileUploads } from './useFileUploads';
import { useNotionPlan } from './useNotionPlan';
import { useWebhookTrigger } from './useWebhookTrigger';

export const useChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false);
  const [messagesSinceLastCheck, setMessagesSinceLastCheck] = useState(0);
  
  const { toast } = useToast();
  const openAIApiKey = import.meta.env.VITE_OPENAI_API_KEY || '';
  
  const {
    uploadedFiles,
    setUploadedFiles,
    isUploading,
    setIsUploading,
    handleFileUpload,
    removeFile
  } = useFileUploads();

  const {
    notionUrl,
    generateNotionPlan
  } = useNotionPlan(messages, setMessages);

  const {
    triggerWebhook,
    isWebhookTriggering
  } = useWebhookTrigger();

  useEffect(() => {
    const savedMessages = loadChatHistory();
    if (savedMessages && savedMessages.length > 0) {
      setMessages(savedMessages);
      setHasUserSentMessage(savedMessages.some(msg => msg.sender === 'user'));
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      saveChatHistory(messages);
    }
  }, [messages]);

  const handleNotionRequest = async (messageText: string) => {
    setMessages(prev => [...prev, {
      sender: 'user',
      text: messageText,
      files: uploadedFiles.length > 0 ? [...uploadedFiles] : undefined
    }]);
    
    setInputMessage('');
    setUploadedFiles([]);
    setIsTyping(true);
    
    const url = await generateNotionPlan();
    console.log("Generated Notion Plan URL:", url);
    
    setIsTyping(false);
  };

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim() && uploadedFiles.length === 0) return;

    const finalMessageText = messageText.trim() ? messageText : "Shared file(s)";

    const lowerCaseMessage = finalMessageText.toLowerCase();
    if (messages.length >= 4 && 
        (lowerCaseMessage.includes('notion') || 
         lowerCaseMessage.includes('plan') || 
         lowerCaseMessage.includes('document') ||
         lowerCaseMessage.includes('get my plan'))) {
      handleNotionRequest(finalMessageText);
      return;
    }

    setMessages(prev => [...prev, {
      sender: 'user',
      text: finalMessageText,
      files: uploadedFiles.length > 0 ? [...uploadedFiles] : undefined
    }]);
    
    setInputMessage('');
    setUploadedFiles([]);
    setHasUserSentMessage(true);
    setIsTyping(true);
    setMessagesSinceLastCheck(prev => prev + 1);

    try {
      const conversationHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));
      
      const systemMessage = {
        role: 'system',
        content: 'You are KnightAI, focused on quickly qualifying business interest to generate a Notion plan. Your goal is to understand the user\'s basic business needs with minimal questions. Focus on identifying their unique selling points early - these are the specific advantages, features, methodologies, or values that make their business stand out from competitors. After gathering essential information (unique selling points, target audience, core services, current marketing), mention that a detailed plan matching their interests will be prepared in a Notion document that will be shared with them. Don\'t ask more than 1-2 follow-up questions unless they\'re creating strong engagement. If the user hasn\'t mentioned unique selling points, politely ask about them as they\'re crucial for the Notion plan.'
      };
      
      conversationHistory.unshift(systemMessage);
      conversationHistory.push({
        role: 'user',
        content: finalMessageText
      });

      const aiResponse = await sendMessageToOpenAI(conversationHistory, openAIApiKey);
      
      setIsTyping(false);
      setMessages(prev => [...prev, {
        sender: 'ai',
        text: aiResponse
      }]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      setIsTyping(false);
      
      toast({
        title: "Failed to get AI response",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
      
      setMessages(prev => [...prev, {
        sender: 'ai',
        text: "I'm unable to qualify your business interests right now. Let's reconnect momentarily."
      }]);
    }
  };

  useEffect(() => {
    const checkAndTriggerWebhook = async () => {
      if (messages.length >= 3 && !isWebhookTriggering) {
        const context = messages
          .map(msg => `${msg.sender === 'user' ? 'Customer' : 'AI'}: ${msg.text}`)
          .join('\n\n');
        
        await triggerWebhook(context);
      }
    };

    checkAndTriggerWebhook();
  }, [messages, isWebhookTriggering, triggerWebhook]);

  useEffect(() => {
    const handleAiMessage = (event: CustomEvent<{ message: string }>) => {
      setMessages(prev => [...prev, {
        sender: 'ai',
        text: event.detail.message
      }]);
    };

    window.addEventListener('ai-message', handleAiMessage as EventListener);
    return () => {
      window.removeEventListener('ai-message', handleAiMessage as EventListener);
    };
  }, []);

  return {
    messages,
    setMessages,
    inputMessage,
    setInputMessage,
    isTyping,
    uploadedFiles,
    setUploadedFiles,
    isUploading,
    setIsUploading,
    hasUserSentMessage,
    setHasUserSentMessage,
    handleSendMessage,
    handleNotionRequest,
    notionUrl,
    removeFile: removeFile,
    handleFileUpload
  };
};
